/**
 * The eCTD document (leaf) picker behind "Draft more sections" — pure, so it
 * can be tested without a DOM (web/ has no DOM-test harness; every UI decision
 * lives in a module like this one and the component stays layout). Sibling to
 * `draftTree.ts`, which this module falls back to when a doctype has no
 * leaves (`catalog.documents` is `null`/absent) — that fallback is the
 * invariant 14 doctypes depend on.
 *
 * CTD Module 3 is filed as ~55-82 separate documents ("eCTD leaves"), never as
 * one file. `catalog.documents` is the wire shape `GET /api/doc-types/{doc_type}`
 * already serves — RESOLVED server-side by `src/csr_sim/doc_types/documents.py`
 * (`effective_documents`); this module never re-derives the partition, it only
 * maps modules onto the leaves that partition already declares.
 */

/** The shape this module needs from `DocTypeModules` — a structural subset, so
 *  tests can build a catalogue without the full config. */
export interface DocumentPickerCatalog {
  /** Optional and `readonly`-tolerant so BOTH surfaces' own doctype types
   *  satisfy it structurally — asthra-v4-web's `DocTypeConfig` declares
   *  `modules` optional and deeply readonly, the add-in's does not. Widening
   *  the shared type is the point: a surface that has to adapt its config at
   *  the call site is one refactor away from adapting it differently. Read-only
   *  is also honest — nothing here mutates a catalog. */
  modules?: Readonly<Record<string, { readonly title?: string; readonly sections?: readonly string[] }>>;
  /** The eCTD-leaf partition, in submission order. `null`/absent/empty for a
   *  doctype with no `documents:` block — the picker does not apply. */
  documents?: readonly { readonly id: string; readonly title: string; readonly sections: readonly string[] }[] | null;
}

/** One row in the document picker: an eCTD leaf, and the modules it collects. */
export interface DocumentRow {
  id: string;
  title: string;
  /** Undrafted (unlocked) modules in this leaf — exactly the set a selection
   *  of this row would send to a run. Already-drafted modules are counted in
   *  `total`/`drafted` but never appear here, so callers never need to
   *  re-filter by lock state (mirrors "Draft More never redrafts"). */
  moduleIds: string[];
  /** Every module in the leaf, drafted or not — the denominator of `statusLabel`. */
  total: number;
  /** Already-drafted (locked) modules in the leaf. */
  drafted: number;
  status: "not-started" | "partial" | "drafted";
  statusLabel: string;
}

/**
 * Which document (if any) a module belongs to, mirroring the three-rule
 * cascade `documents.py`'s `_module_document_claims` implements server-side:
 *
 * 1. **Own id** — if the module's own id is itself a section some document
 *    claims, resolution is via that id alone. Always wins.
 * 2. **Declared sections** — otherwise, the module's own declared `sections`
 *    are walked in order for the first one some document claims.
 *
 * Never `sections[0]` in isolation for rule 1's case: a module can declare an
 * ancestor heading as its first section (it is the module that EMITS that
 * heading on export — see `draftTree.ts`'s `resolveModuleForNumber` for the
 * sibling bug this mirrors), which can belong to a DIFFERENT document than
 * the one the module's own id names.
 */
function resolveDocumentForModule(
  moduleId: string,
  declaredSections: readonly string[],
  sectionToDocId: Map<string, string>,
): string | undefined {
  const own = sectionToDocId.get(moduleId);
  if (own !== undefined) return own;
  for (const section of declaredSections) {
    const found = sectionToDocId.get(section);
    if (found !== undefined) return found;
  }
  return undefined;
}

/** section/module id -> the document that declares it. First document wins
 *  on a duplicate claim, matching declaration order — a defensive tiebreak;
 *  a well-formed `documents:` block never has one section claimed twice. */
function buildSectionToDocId(
  documents: readonly { readonly id: string; readonly sections: readonly string[] }[],
): Map<string, string> {
  const sectionToDocId = new Map<string, string>();
  for (const doc of documents) {
    for (const section of doc.sections) {
      if (!sectionToDocId.has(section)) sectionToDocId.set(section, doc.id);
    }
  }
  return sectionToDocId;
}

/**
 * Every module in the catalogue mapped to the document it belongs to — the
 * join Task 2's expand view needs. `DocumentRow.moduleIds` only holds the
 * UNDRAFTED subset of a leaf's modules (see the field doc above), so
 * rendering a leaf's already-drafted sections (the mockup's
 * `☐ 3.2.P.3.2 Batch Formula (drafted)` row) requires recovering the FULL
 * membership — locked and unlocked — some other way. This is that other way:
 * callers filter their own full, locked-inclusive module list (e.g.
 * `draftTree.ts`'s `TreeNode[]`, built with the same `isLocked` callback) down
 * to one leaf by checking `moduleDocumentMap(catalog).get(moduleId) ===
 * doc.id`, rather than re-deriving titles/ordering that `draftTree.ts` already
 * gets right.
 *
 * Returns an empty map under the same null-catalogue invariant as
 * `buildDocumentRows` (no `documents:` block ⇒ nothing to map).
 */
export function moduleDocumentMap(catalog: DocumentPickerCatalog | null | undefined): Map<string, string> {
  const documents = catalog?.documents;
  const result = new Map<string, string>();
  if (!documents || documents.length === 0) return result;

  const sectionToDocId = buildSectionToDocId(documents);
  for (const [moduleId, info] of Object.entries(catalog?.modules ?? {})) {
    const docId = resolveDocumentForModule(moduleId, info.sections ?? [], sectionToDocId);
    if (docId !== undefined) result.set(moduleId, docId);
  }
  return result;
}

/**
 * Build the document-picker rows, or `null` when the doctype declares no
 * leaves — the caller's cue to fall back to `buildDraftTree` unchanged. Rows
 * come back in declaration order (submission order), never sorted.
 *
 * `isLocked` decides which modules are already drafted (the caller reads that
 * off the spine) — kept as a callback so this module never needs to know the
 * spine's shape, same convention as `buildDraftTree`.
 */
export function buildDocumentRows(
  catalog: DocumentPickerCatalog | null | undefined,
  isLocked: (moduleId: string) => boolean,
): DocumentRow[] | null {
  const documents = catalog?.documents;
  if (!documents || documents.length === 0) return null;

  // Shares its resolution logic with `moduleDocumentMap` (own id first, then
  // declared sections) rather than re-deriving it here.
  const moduleToDoc = moduleDocumentMap(catalog);
  const modulesByDoc = new Map<string, string[]>();
  for (const doc of documents) modulesByDoc.set(doc.id, []);
  for (const [moduleId, docId] of moduleToDoc) modulesByDoc.get(docId)?.push(moduleId);

  return documents.map((doc) => {
    const allIds = modulesByDoc.get(doc.id) ?? [];
    const moduleIds: string[] = [];
    let drafted = 0;
    for (const mid of allIds) {
      if (isLocked(mid)) drafted++;
      else moduleIds.push(mid);
    }
    const total = allIds.length;

    let status: DocumentRow["status"];
    let statusLabel: string;
    if (total > 0 && drafted === total) {
      status = "drafted";
      statusLabel = "drafted";
    } else if (drafted > 0) {
      status = "partial";
      statusLabel = `${drafted} of ${total} sections`;
    } else {
      status = "not-started";
      statusLabel = "not started";
    }

    return { id: doc.id, title: doc.title, moduleIds, total, drafted, status, statusLabel };
  });
}

/**
 * The module ids a selection of document rows resolves to: the union across
 * every selected leaf, de-duplicated, in row order. Locked modules are never
 * included — `moduleIds` on each row already excludes them (Draft More never
 * redrafts a drafted section), so a leaf whose modules are all locked
 * contributes nothing even if it is itself selected.
 */
export function modulesForDocuments(rows: DocumentRow[], selectedIds: Set<string>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of rows) {
    if (!selectedIds.has(row.id)) continue;
    for (const moduleId of row.moduleIds) {
      if (seen.has(moduleId)) continue;
      seen.add(moduleId);
      out.push(moduleId);
    }
  }
  return out;
}

/** Footer summary for a selection: independently-pluralised document/section
 *  counts, e.g. "1 document · 3 sections", "2 documents · 1 section". The
 *  section count is exactly `modulesForDocuments(...).length` — what a run
 *  started from this selection would actually draft. */
export function documentSelectionSummary(rows: DocumentRow[], selectedIds: Set<string>): string {
  const docCount = rows.filter((row) => selectedIds.has(row.id)).length;
  const sectionCount = modulesForDocuments(rows, selectedIds).length;
  const docWord = docCount === 1 ? "document" : "documents";
  const sectionWord = sectionCount === 1 ? "section" : "sections";
  return `${docCount} ${docWord} · ${sectionCount} ${sectionWord}`;
}

/**
 * A document row's checkbox tri-state, given the fine-grained set of
 * currently-checked module ids — the same `Set<string>` the dialog already
 * keeps for individual section checkboxes (unchanged by this feature; a
 * document row is just another way to toggle members of it, exactly like a
 * `draftTree.ts` group header toggles its descendants). A leaf with no
 * undrafted modules (fully drafted, or nothing resolved to it) is "none":
 * callers distinguish that "nothing to select" case from "not selected"
 * themselves via `row.moduleIds.length === 0`, same as `buildDraftTree`'s
 * `headerDescendants` emptiness drives its own header disabling.
 */
export function documentRowSelectionState(
  row: DocumentRow,
  selectedModuleIds: Set<string>,
): "none" | "partial" | "all" {
  if (row.moduleIds.length === 0) return "none";
  const checked = row.moduleIds.filter((id) => selectedModuleIds.has(id)).length;
  if (checked === 0) return "none";
  return checked === row.moduleIds.length ? "all" : "partial";
}

/**
 * Bridges the fine-grained selection the dialog keeps (individual module ids
 * — so per-section deselect and partial drafting survive expand/collapse)
 * back onto the whole-row contract `modulesForDocuments` and
 * `documentSelectionSummary` expect: a document counts as "selected" once it
 * has at least one checked module, and each row only ever contributes the
 * modules actually checked — not its full undrafted set — so an unchecked
 * sibling inside a partially-selected leaf can never leak into the run via
 * `modulesForDocuments(narrowed.rows, narrowed.selectedIds)`.
 */
export function narrowToSelectedModules(
  rows: DocumentRow[],
  selectedModuleIds: Set<string>,
): { rows: DocumentRow[]; selectedIds: Set<string> } {
  const narrowed = rows.map((row) => ({
    ...row,
    moduleIds: row.moduleIds.filter((id) => selectedModuleIds.has(id)),
  }));
  const selectedIds = new Set(narrowed.filter((row) => row.moduleIds.length > 0).map((row) => row.id));
  return { rows: narrowed, selectedIds };
}

/** One leaf, offered at document creation: the whole of what the choice is. */
export interface LeafChoice {
  id: string;
  title: string;
}

/**
 * The leaves a NEW document may be created as — `null` when the doctype
 * declares none.
 *
 * One Asthra document IS one eCTD leaf: the writer picks exactly one of these
 * at creation and that document is that leaf (a full CTD Module 3 becomes ~66
 * separate documents, one per leaf). Deliberately NOT `buildDocumentRows`: a
 * leaf being created has no spine, so drafted/undrafted counts and the
 * selection tri-state have nothing to describe — there is nothing to be
 * partially drafted yet. Same null contract as `buildDocumentRows` though, and
 * for the same reason: `null` is the 14 single-document doctypes' cue that
 * this whole axis does not apply to them — no picker, no leaf id, no change.
 *
 * Order is `catalog.documents`' own order — submission (skeleton) order, as
 * `effective_documents` resolved it server-side. Never re-sorted, never
 * re-derived.
 */
export function leafChoices(catalog: DocumentPickerCatalog | null | undefined): LeafChoice[] | null {
  const documents = catalog?.documents;
  if (!documents || documents.length === 0) return null;
  return documents.map((doc) => ({ id: doc.id, title: doc.title }));
}

/**
 * Whether the leaf step of the creation dialog is satisfied — "exactly one",
 * expressed as a gate rather than as a widget, so the rule is testable without
 * a DOM.
 *
 * `true` when the doctype offers no leaves (`choices === null`): there is
 * nothing to pick, and a doctype that is legitimately one document must never
 * be blocked by a step that does not apply to it — a leaf id left over from a
 * previously-selected doctype is ignored, not treated as a choice.
 *
 * Otherwise exactly one of the OFFERED leaves must be chosen. Membership is
 * checked, not mere non-emptiness, because the dialog keeps one `docType` and
 * one `leafDocumentId` in state: switching doctype leaves the previous
 * doctype's leaf id behind, and a stale id must not satisfy the gate (it would
 * be rejected server-side by `server/leaf_identity.py` anyway, silently losing
 * the identity).
 */
export function leafSelectionReady(choices: LeafChoice[] | null, selectedId: string | null | undefined): boolean {
  if (choices === null) return true;
  return choices.some((choice) => choice.id === selectedId);
}
